// src/components/pages/.../InquiryHistory.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  IconButton,
  Chip,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  Divider,
  Button,
  useMediaQuery,
  Tooltip,
  TextField,
  InputAdornment,
  Link as MLink,
  MenuItem,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import CancelRoundedIcon from "@mui/icons-material/CancelRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ArrowUpwardRoundedIcon from "@mui/icons-material/ArrowUpwardRounded";
import ArrowDownwardRoundedIcon from "@mui/icons-material/ArrowDownwardRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import ClearRoundedIcon from "@mui/icons-material/ClearRounded";
import { InquiryAPI } from "../../../../../api/Inquiry";

import { useAuth } from "../../../Auth/AuthContext";
import { useNavigate, useLocation } from "react-router-dom";
import { getLangFromCookie } from "../../../../../Utils/LangCookie";

// ✅ SAME reference style as other inquiry page
import { http, resolveAssetUrl } from "../../../../../lib/http";

/* ---------------- WP REST helpers (same as other inquiry page) ---------------- */

function normalizeLang(lang) {
  const s = String(lang || "")
    .toUpperCase()
    .trim();
  if (s === "EN" || s === "JP" || s === "ZH") return s;
  return "ZH";
}

function getWpOrigin() {
  const raw =
    process.env.REACT_APP_WP_ORIGIN ||
    process.env.REACT_APP_WP_GRAPHQL_URL?.replace(/\/graphql\/?$/, "") ||
    "http://localhost/wp-headless";
  return String(raw).replace(/\/+$/, "");
}

function wpRestBase() {
  // your plugin namespace is "api"
  return `${getWpOrigin()}/wp-json/api`;
}

function withLangQS(url, lang) {
  const L = normalizeLang(lang);
  return (
    url + (url.includes("?") ? "&" : "?") + `lang=${encodeURIComponent(L)}`
  );
}

/* ---------------- constants ---------------- */

const PLACEHOLDER_IMG = "/img/placeholder.png";

function resolveImgUrl(raw) {
  // ✅ always use the same resolver as ProductCard/ProductInfo
  const u = resolveAssetUrl(raw);
  return u || PLACEHOLDER_IMG;
}

/* ---------------- i18n ---------------- */

const I18N = {
  title: {
    ZH: "詢價歷史紀錄",
    EN: "Inquiry History",
    JP: "見積履歴",
  },
  empty: {
    ZH: "目前沒有歷史詢價紀錄。",
    EN: "No inquiry history yet.",
    JP: "見積履歴はまだありません。",
  },
  loading: {
    ZH: "載入中…",
    EN: "Loading…",
    JP: "読み込み中…",
  },
  table: {
    inquiryID: { ZH: "詢價編號", EN: "Inquiry ID", JP: "見積番号" },
    name: { ZH: "姓名", EN: "Name", JP: "氏名" },
    company: { ZH: "公司名稱", EN: "Company", JP: "会社名" },
    date: { ZH: "日期", EN: "Date", JP: "日付" },
    status: { ZH: "狀態", EN: "Status", JP: "ステータス" },
    view: { ZH: "查看", EN: "View", JP: "詳細" },
  },
  dialog: {
    title: { ZH: "詢價詳細", EN: "Inquiry Details", JP: "見積詳細" },
    close: { ZH: "關閉", EN: "Close", JP: "閉じる" },
    sectionInfo: { ZH: "聯絡資料", EN: "Contact Info", JP: "連絡先情報" },
    sectionItems: { ZH: "詢價商品", EN: "Items", JP: "見積商品" },
    fields: {
      name: { ZH: "姓名", EN: "Name", JP: "氏名" },
      phone: { ZH: "聯絡電話", EN: "Phone", JP: "電話番号" },
      email: { ZH: "電子郵件", EN: "Email", JP: "メール" },
      county: { ZH: "縣市", EN: "County/City", JP: "県・市" },
      address: { ZH: "地址", EN: "Address", JP: "住所" },
      company: { ZH: "公司名稱", EN: "Company", JP: "会社名" },
      industry: { ZH: "產業別", EN: "Industry", JP: "業種" },
      message: { ZH: "訊息", EN: "Message", JP: "メッセージ" },
    },
    itemsTable: {
      image: { ZH: "圖片", EN: "Image", JP: "画像" },
      productID: { ZH: "產品型號", EN: "Product ID", JP: "型番" },
      productName: { ZH: "產品名稱", EN: "Product", JP: "品名" },
      qty: { ZH: "數量", EN: "Qty", JP: "数量" },
      viewImg: { ZH: "看圖片", EN: "View image", JP: "画像を見る" },
    },
  },
  status: {
    pending: { ZH: "未送出", EN: "Pending", JP: "未送信" },
    checkedout: { ZH: "已送出", EN: "Submitted", JP: "送信する" },
    processing: { ZH: "處理中", EN: "Processing", JP: "処理中" },
    finished: { ZH: "已完成", EN: "Finished", JP: "完了" },
    canceled: { ZH: "已取消", EN: "Canceled", JP: "キャンセル" },
    unknown: { ZH: "未知", EN: "Unknown", JP: "不明" },
  },
  seeMore: {
    ZH: "看詳情",
    EN: "See Detail",
    JP: "詳細を見る",
  },
  search: {
    placeholder: {
      ZH: "搜尋：編號 / 姓名 / 公司",
      EN: "Search: ID / name / company",
      JP: "検索：番号 / 氏名 / 会社",
    },
    clear: { ZH: "清除", EN: "Clear", JP: "クリア" },
  },
  filter: {
    status: { ZH: "狀態", EN: "Status", JP: "ステータス" },
    allStatus: { ZH: "全部狀態", EN: "All Status", JP: "すべて" },
    dateFrom: { ZH: "開始日期", EN: "From", JP: "開始日" },
    dateTo: { ZH: "結束日期", EN: "To", JP: "終了日" },
  },
  cancel: {
    button: { ZH: "取消詢價", EN: "Cancel", JP: "キャンセル" },
    title: { ZH: "取消詢價", EN: "Cancel Inquiry", JP: "見積をキャンセル" },
    message: {
      ZH: "確定要取消此詢價嗎？取消後系統將寄送通知信。",
      EN: "Are you sure you want to cancel this inquiry? A notification email will be sent after cancellation.",
      JP: "この見積をキャンセルしてもよろしいですか？キャンセル後、通知メールが送信されます。",
    },
    confirm: { ZH: "確認取消", EN: "Confirm Cancel", JP: "キャンセル確認" },
    failed: {
      ZH: "取消詢價失敗，請稍後再試。",
      EN: "Failed to cancel inquiry. Please try again later.",
      JP: "見積のキャンセルに失敗しました。後でもう一度お試しください。",
    },
  },
};

/* ---------------- helpers ---------------- */

const safe = (v) => (v == null || v === "" ? "-" : String(v));

function statusLabel(lang, s) {
  const hit = I18N.status[s] || I18N.status.unknown;
  return hit[lang] || hit.ZH;
}

function statusChipColor(s) {
  if (s === "checkedout") return "info";
  if (s === "processing") return "warning";
  if (s === "finished") return "success";
  if (s === "canceled") return "error";
  return "default";
}

/**
 * Some stored info keys may accidentally be saved as:
 *   "u59d3u540d" instead of "姓名"
 *   "u7e23u5e02" instead of "縣市"
 * so we read BOTH forms.
 */
function zhKeyToUxxxx(zhKey) {
  const s = String(zhKey || "");
  let out = "";
  for (const ch of s) {
    const hex = ch.codePointAt(0).toString(16).padStart(4, "0");
    out += `u${hex}`;
  }
  return out;
}

function getInfoField(info, zhKey) {
  if (!info || typeof info !== "object") return undefined;
  if (info[zhKey] != null && info[zhKey] !== "") return info[zhKey];

  const ukey = zhKeyToUxxxx(zhKey);
  if (info[ukey] != null && info[ukey] !== "") return info[ukey];

  // also accept raw "\u59d3\u540d" style if it ever appears as a literal key
  const escaped = zhKey
    .split("")
    .map((ch) => `\\u${ch.codePointAt(0).toString(16).padStart(4, "0")}`)
    .join("");
  if (info[escaped] != null && info[escaped] !== "") return info[escaped];

  return undefined;
}

/* ---- date parser (for sorting) ----
   handles: "dd/mm/yyyy - HH:MM:SS" and "dd/mm/yy"
*/
function parseDmyTimestamp(s) {
  const str = String(s || "").trim();
  const m = str.match(
    /^(\d{2})\/(\d{2})\/(\d{2}|\d{4})(?:\s*-\s*(\d{2}):(\d{2}):(\d{2}))?$/,
  );
  if (!m) return null;
  const dd = parseInt(m[1], 10);
  const mm = parseInt(m[2], 10);
  let yyyy = parseInt(m[3], 10);
  if (yyyy < 100) yyyy += 2000;
  const HH = parseInt(m[4] || "0", 10);
  const MI = parseInt(m[5] || "0", 10);
  const SS = parseInt(m[6] || "0", 10);
  const d = new Date(yyyy, mm - 1, dd, HH, MI, SS);
  return Number.isNaN(d.getTime()) ? null : d.getTime();
}

function parseDmyToDateInputValue(s) {
  const str = String(s || "").trim();
  const m = str.match(/^(\d{2})\/(\d{2})\/(\d{2}|\d{4})/);
  if (!m) return "";

  let yyyy = parseInt(m[3], 10);
  if (yyyy < 100) yyyy += 2000;

  return `${yyyy}-${m[2]}-${m[1]}`;
}

function resolveUserID(user) {
  return (
    user?.id ||
    user?.userID ||
    user?.sehoUserID ||
    user?.uid ||
    user?.memberID ||
    user?.username ||
    ""
  );
}

function KV({ label, value, fontFamily }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography
        sx={{
          fontFamily,
          fontWeight: 700,
          fontSize: 13,
          color: "text.secondary",
          mb: 0.5,
          letterSpacing: 0.2,
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontFamily,
          fontWeight: 600,
          fontSize: 15,
          wordBreak: "break-word",
        }}
      >
        {safe(value)}
      </Typography>
    </Box>
  );
}

/* ---------------- Mobile row card (history list) ---------------- */

function MobileRowCard({ r, lang, safeFont, onView, onCancel }) {
  const info = r?.info || null;
  const name = getInfoField(info, "姓名") || "-";
  const company = getInfoField(info, "公司名稱") || "-";
  const date = r?.inquiredDate || r?.finishedDate || "-";
  const s = r?.status || "unknown";

  const Label = ({ children }) => (
    <Typography
      sx={{
        fontFamily: safeFont[lang],
        fontSize: 12,
        fontWeight: 700,
        color: "text.secondary",
        mr: 0.75,
        flexShrink: 0,
      }}
    >
      {children}：
    </Typography>
  );

  const Value = ({ children, bold }) => (
    <Typography
      sx={{
        fontFamily: safeFont[lang],
        fontSize: 13,
        fontWeight: bold ? 700 : 400,
        color: bold ? "text.primary" : "text.secondary",
        minWidth: 0,
        overflowWrap: "anywhere",
        wordBreak: "break-word",
        lineHeight: 1.3,
      }}
    >
      {children}
    </Typography>
  );

  const Line = ({ label, children }) => (
    <Stack
      direction="row"
      spacing={0}
      alignItems="flex-start"
      sx={{ minWidth: 0 }}
    >
      <Label>{label}</Label>
      <Box sx={{ minWidth: 0, flex: 1 }}>{children}</Box>
    </Stack>
  );

  return (
    <Paper
      variant="outlined"
      sx={{
        borderRadius: 2,
        p: 1.5,
        display: "grid",
        gridTemplateColumns: "1fr auto",
        gap: 1.25,
        alignItems: "start",
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Line label={I18N.table.inquiryID[lang]}>
          <Value bold>{safe(r?.inquiryID)}</Value>
        </Line>

        <Box sx={{ mt: 0.6 }}>
          <Line label={I18N.table.name[lang]}>
            <Value>{safe(name)}</Value>
          </Line>
        </Box>

        <Box sx={{ mt: 0.6 }}>
          <Line label={I18N.table.company[lang]}>
            <Value>{safe(company)}</Value>
          </Line>
        </Box>

        <Box sx={{ mt: 0.8 }}>
          <Line label={I18N.table.status[lang]}>
            <Value>{statusLabel(lang, s)}</Value>
          </Line>
        </Box>

        <Box sx={{ mt: 0.8 }}>
          <Line label={I18N.table.date[lang]}>
            <Value>{safe(date)}</Value>
          </Line>
        </Box>
      </Box>

      <Stack spacing={0.75} alignItems="flex-end">
        <Tooltip title={I18N.seeMore[lang]} arrow enterDelay={200}>
          <IconButton
            size="small"
            onClick={onView}
            aria-label="view"
            sx={{
              border: "1px solid rgba(0,0,0,0.12)",
              borderRadius: 2,
            }}
          >
            <VisibilityRoundedIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        {s === "checkedout" ? (
          <Tooltip title={I18N.cancel.button[lang]} arrow enterDelay={200}>
            <IconButton
              size="small"
              onClick={onCancel}
              aria-label="cancel inquiry"
              sx={{
                border: "1px solid rgba(211,47,47,0.35)",
                borderRadius: 2,
                color: "error.main",
              }}
            >
              <CancelRoundedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ) : null}
      </Stack>
    </Paper>
  );
}

/* ---------------- Mobile item card (dialog items) ---------------- */

function DialogItemCard({ it, lang, safeFont, onGoProduct }) {
  const img = resolveImgUrl(it?.image || it?.images?.[0] || "");
  return (
    <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 2 }}>
      <Stack spacing={1}>
        <Stack direction="row" spacing={1.25} alignItems="center">
          <Box
            component="img"
            src={img}
            alt={safe(it?.productName)}
            onError={(e) => (e.currentTarget.src = PLACEHOLDER_IMG)}
            sx={{
              width: 72,
              height: 54,
              objectFit: "contain",
              borderRadius: 1,
              border: "1px solid",
              borderColor: "divider",
              bgcolor: "#fff",
              p: 0.5,
              flexShrink: 0,
            }}
          />

          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              sx={{
                fontFamily: safeFont[lang],
                fontSize: 12,
                fontWeight: 700,
                color: "text.secondary",
                mb: 0.25,
              }}
            >
              {I18N.dialog.itemsTable.productID[lang]}
            </Typography>
            <Typography
              sx={{
                fontFamily: safeFont[lang],
                fontSize: 13,
                fontWeight: 700,
                overflowWrap: "anywhere",
              }}
            >
              {safe(it?.productID)}
            </Typography>
          </Box>

          <Box sx={{ textAlign: "right" }}>
            <Typography
              sx={{
                fontFamily: safeFont[lang],
                fontSize: 12,
                fontWeight: 700,
                color: "text.secondary",
                mb: 0.25,
              }}
            >
              {I18N.dialog.itemsTable.qty[lang]}
            </Typography>
            <Typography
              sx={{ fontFamily: safeFont[lang], fontSize: 13, fontWeight: 700 }}
            >
              {safe(it?.qty)}
            </Typography>
          </Box>
        </Stack>

        <Box>
          <Typography
            sx={{
              fontFamily: safeFont[lang],
              fontSize: 12,
              fontWeight: 700,
              color: "text.secondary",
              mb: 0.25,
            }}
          >
            {I18N.dialog.itemsTable.productName[lang]}
          </Typography>

          <MLink
            component="button"
            underline="hover"
            onClick={onGoProduct}
            sx={{
              fontFamily: safeFont[lang],
              fontSize: 13,
              fontWeight: 700,
              textAlign: "left",
              cursor: "pointer",
              color: "primary.main",
              overflowWrap: "anywhere",
            }}
          >
            {safe(it?.productName)}
          </MLink>
        </Box>
      </Stack>
    </Paper>
  );
}

/* ---------------- component ---------------- */

export default function InquiryHistory({ font }) {
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up("md"));
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const nav = useNavigate();
  const loc = useLocation();
  const { user, loading: authLoading } = useAuth();

  const safeFont = useMemo(() => {
    const fallback = {
      ZH: "LINE Seed TW",
      EN: "LINE Seed Sans",
      JP: "LINE Seed JP",
    };
    if (!font || typeof font !== "object") return fallback;
    return {
      ZH: font.ZH || fallback.ZH,
      EN: font.EN || fallback.EN,
      JP: font.JP || fallback.JP,
    };
  }, [font]);

  const [lang, setLang] = useState(() =>
    normalizeLang(getLangFromCookie() || "ZH"),
  );

  useEffect(() => {
    const syncLang = () => setLang(normalizeLang(getLangFromCookie() || "ZH"));
    syncLang();
    window.addEventListener("app:languageChanged", syncLang);
    return () => window.removeEventListener("app:languageChanged", syncLang);
  }, []);

  useEffect(() => {
    if (!authLoading && !user) {
      nav("/auth", { replace: false, state: { redirectTo: loc.pathname } });
    }
  }, [authLoading, user, nav, loc.pathname]);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [cancelError, setCancelError] = useState("");

  // ✅ Filter state
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  // ✅ Sort state
  const [sortKey, setSortKey] = useState("date"); // inquiryID | name | company | date | status
  const [sortDir, setSortDir] = useState("desc"); // asc | desc

  const userID = useMemo(() => resolveUserID(user) || null, [user]);

  useEffect(() => {
    let alive = true;

    (async () => {
      if (authLoading) return;

      if (!userID) {
        if (alive) {
          setRows([]);
          setLoading(false);
        }
        return;
      }

      setLoading(true);
      try {
        const data = await InquiryAPI.historyNonPending(userID, { lang });
        if (!alive) return;
        setRows(Array.isArray(data) ? data : []);
      } catch (e) {
        if (!alive) return;
        console.error("history load failed:", e);
        setRows([]);
      } finally {
        if (!alive) return;
        setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [authLoading, userID, lang]);

  const handleView = (row) => {
    setSelected(row);
    setOpen(true);
  };

  const handleCancelOpen = (row) => {
    if (!row || row.status !== "checkedout") return;

    setCancelError("");
    setCancelTarget(row);
  };

  const handleCancelClose = () => {
    if (cancelLoading) return;

    setCancelTarget(null);
    setCancelError("");
  };

  const handleCancelConfirm = async () => {
    if (!cancelTarget?.inquiryID || !userID) return;

    setCancelLoading(true);
    setCancelError("");

    try {
      const result = await InquiryAPI.cancel(cancelTarget.inquiryID, userID, {
        lang,
      });

      const updatedInquiry = result?.inquiry || null;

      setRows((prev) =>
        (prev || []).map((row) => {
          if (row?.inquiryID !== cancelTarget.inquiryID) return row;

          return updatedInquiry
            ? {
                ...row,
                ...updatedInquiry,
                status: "canceled",
              }
            : {
                ...row,
                status: "canceled",
              };
        }),
      );

      window.dispatchEvent(new CustomEvent("seho:inquiryChanged"));

      setCancelTarget(null);
    } catch (e) {
      console.error("cancel inquiry failed:", e);
      setCancelError(I18N.cancel.failed[lang]);
    } finally {
      setCancelLoading(false);
    }
  };

  const info = selected?.info || null;
  const items = selected?.items || [];

  const closeDialog = () => setOpen(false);

  // ✅ go to ProductDetailPageB2B with parentID + select variant by productID
  const goToProduct = (it) => {
    const parentID = it?.parentID || it?.product?.parentID || null;
    const variantID = it?.productID || null;

    if (!parentID || !variantID) return;

    closeDialog();

    nav(
      `/商品資訊/${encodeURIComponent(parentID)}?variant=${encodeURIComponent(
        variantID,
      )}`,
      {
        state: {
          fromBreadcrumbs: [{ label: "商品資訊", href: "b2b/商品資訊" }],
        },
      },
    );
  };

  // ✅ Filtering + sorting
  const filteredSorted = useMemo(() => {
    const q = String(query || "")
      .trim()
      .toLowerCase();

    const fromTime = dateFrom
      ? new Date(`${dateFrom}T00:00:00`).getTime()
      : null;
    const toTime = dateTo ? new Date(`${dateTo}T23:59:59`).getTime() : null;

    const filtered = (rows || []).filter((r) => {
      const inf = r?.info || null;

      const name = safe(getInfoField(inf, "姓名")).toLowerCase();
      const company = safe(getInfoField(inf, "公司名稱")).toLowerCase();
      const inquiryID = safe(r?.inquiryID).toLowerCase();
      const statusRaw = safe(r?.status).toLowerCase();
      const rowDateRaw = r?.inquiredDate || r?.finishedDate || "";
      const rowTime = parseDmyTimestamp(rowDateRaw);

      const hay = [inquiryID, name, company].join(" | ");

      const matchSearch = !q || hay.includes(q);
      const matchStatus = !statusFilter || statusRaw === statusFilter;

      const matchDateFrom =
        fromTime == null || rowTime == null || rowTime >= fromTime;

      const matchDateTo =
        toTime == null || rowTime == null || rowTime <= toTime;

      return matchSearch && matchStatus && matchDateFrom && matchDateTo;
    });

    const getValue = (r) => {
      const inf = r?.info || null;

      if (sortKey === "inquiryID") return safe(r?.inquiryID).toLowerCase();
      if (sortKey === "name")
        return safe(getInfoField(inf, "姓名")).toLowerCase();
      if (sortKey === "company")
        return safe(getInfoField(inf, "公司名稱")).toLowerCase();
      if (sortKey === "status")
        return statusLabel(lang, r?.status || "unknown").toLowerCase();
      if (sortKey === "date") {
        const raw = r?.inquiredDate || r?.finishedDate || "";
        const ts = parseDmyTimestamp(raw);
        return ts == null ? -Infinity : ts;
      }
      return safe(r?.inquiryID).toLowerCase();
    };

    const dir = sortDir === "asc" ? 1 : -1;

    const sorted = [...filtered].sort((a, b) => {
      const av = getValue(a);
      const bv = getValue(b);

      if (typeof av === "number" && typeof bv === "number") {
        if (av === bv) return 0;
        return av > bv ? dir : -dir;
      }

      const as = String(av);
      const bs = String(bv);
      if (as === bs) return 0;
      return as.localeCompare(bs) * dir;
    });

    return sorted;
  }, [rows, query, statusFilter, dateFrom, dateTo, sortKey, sortDir, lang]);

  const toggleSort = (key) => {
    if (sortKey !== key) {
      setSortKey(key);
      setSortDir("asc");
      return;
    }
    setSortDir((d) => (d === "asc" ? "desc" : "asc"));
  };

  const SortHeader = ({ k, label }) => {
    const active = sortKey === k;
    const Icon = !active
      ? null
      : sortDir === "asc"
        ? ArrowUpwardRoundedIcon
        : ArrowDownwardRoundedIcon;

    return (
      <Stack
        direction="row"
        spacing={0.75}
        alignItems="center"
        onClick={() => toggleSort(k)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") toggleSort(k);
        }}
        sx={{
          cursor: "pointer",
          userSelect: "none",
          "&:hover .sortLabel": { textDecoration: "underline" },
        }}
      >
        <Typography
          className="sortLabel"
          sx={{ fontFamily: safeFont[lang], fontWeight: 700 }}
        >
          {label}
        </Typography>
        {Icon ? <Icon sx={{ fontSize: 16, opacity: 0.85 }} /> : null}
      </Stack>
    );
  };

  return (
    <Box sx={{ width: "100%", px: { xs: 1.5, md: 3 }, py: { xs: 2, md: 3 } }}>
      <Typography
        variant="h3"
        align="center"
        sx={{
          fontWeight: 700,
          fontFamily: safeFont[lang],
          color: "#F5A400",
          mb: 1.25,
          fontSize: { xs: 24, sm: 30, md: undefined },
        }}
      >
        {I18N.title[lang]}
      </Typography>

      {/* ✅ FILTER BAR */}
      <Paper
        variant="outlined"
        sx={{
          borderRadius: 2,
          p: 1.25,
          mb: 1.5,
          display: "flex",
          alignItems: "center",
          gap: 1.25,
          flexWrap: "wrap",
        }}
      >
        <TextField
          size="small"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={I18N.search.placeholder[lang]}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchRoundedIcon fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: query ? (
              <InputAdornment position="end">
                <Tooltip title={I18N.search.clear[lang]} arrow>
                  <IconButton
                    size="small"
                    onClick={() => setQuery("")}
                    edge="end"
                  >
                    <ClearRoundedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </InputAdornment>
            ) : null,
          }}
          sx={{
            width: { xs: "100%", md: 360 },
            "& input": { fontFamily: safeFont[lang], fontWeight: 700 },
          }}
        />

        <TextField
          select
          size="small"
          label={I18N.filter.status[lang]}
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          sx={{
            width: { xs: "100%", sm: 180 },
            "& .MuiInputBase-input": {
              fontFamily: safeFont[lang],
              fontWeight: 700,
            },
          }}
        >
          <MenuItem value="">{I18N.filter.allStatus[lang]}</MenuItem>
          <MenuItem value="checkedout">{I18N.status.checkedout[lang]}</MenuItem>
          <MenuItem value="processing">{I18N.status.processing[lang]}</MenuItem>
          <MenuItem value="finished">{I18N.status.finished[lang]}</MenuItem>
          <MenuItem value="canceled">{I18N.status.canceled[lang]}</MenuItem>
        </TextField>

        <TextField
          size="small"
          type="date"
          label={I18N.filter.dateFrom[lang]}
          value={dateFrom}
          onChange={(e) => setDateFrom(e.target.value)}
          InputLabelProps={{ shrink: true }}
          autoComplete="off"
          sx={{
            width: { xs: "100%", sm: 170 },
            "& input": { fontFamily: safeFont[lang], fontWeight: 700 },
          }}
        />

        <Box
          sx={{
            display: { xs: "none", sm: "flex" },
            alignItems: "center",
            justifyContent: "center",
            px: 0.25,
            fontWeight: 700,
            fontSize: 18,
            color: "text.secondary",
          }}
        >
          -
        </Box>

        <TextField
          size="small"
          type="date"
          label={I18N.filter.dateTo[lang]}
          value={dateTo}
          onChange={(e) => setDateTo(e.target.value)}
          InputLabelProps={{ shrink: true }}
          autoComplete="off"
          sx={{
            width: { xs: "100%", sm: 170 },
            "& input": { fontFamily: safeFont[lang], fontWeight: 700 },
          }}
        />

        {(query || statusFilter || dateFrom || dateTo) && (
          <Button
            variant="outlined"
            onClick={() => {
              setQuery("");
              setStatusFilter("");
              setDateFrom("");
              setDateTo("");
            }}
            sx={{
              height: 40,
              fontFamily: safeFont[lang],
              fontWeight: 700,
              whiteSpace: "nowrap",
            }}
          >
            {I18N.search.clear[lang]}
          </Button>
        )}
      </Paper>

      <Paper variant="outlined" sx={{ overflow: "hidden", borderRadius: 2 }}>
        {loading ? (
          <Box sx={{ p: 3 }}>
            <Typography sx={{ fontFamily: safeFont[lang] }}>
              {I18N.loading[lang]}
            </Typography>
          </Box>
        ) : filteredSorted.length === 0 ? (
          <Box sx={{ p: 3 }}>
            <Typography sx={{ fontFamily: safeFont[lang] }}>
              {I18N.empty[lang]}
            </Typography>
          </Box>
        ) : isMobile ? (
          <Box sx={{ p: 1.5 }}>
            <Stack spacing={1.25}>
              {filteredSorted.map((r) => (
                <MobileRowCard
                  key={r.inquiryID}
                  r={r}
                  lang={lang}
                  safeFont={safeFont}
                  onView={() => handleView(r)}
                  onCancel={() => handleCancelOpen(r)}
                />
              ))}
            </Stack>
          </Box>
        ) : (
          <TableContainer sx={{ width: "100%", overflowX: "auto" }}>
            <Table size="medium" sx={{ minWidth: 700 }}>
              <TableHead sx={{ bgcolor: "rgba(0,0,0,0.03)" }}>
                <TableRow>
                  <TableCell
                    sx={{ fontFamily: safeFont[lang], fontWeight: 700 }}
                  >
                    <SortHeader
                      k="inquiryID"
                      label={I18N.table.inquiryID[lang]}
                    />
                  </TableCell>

                  <TableCell
                    sx={{ fontFamily: safeFont[lang], fontWeight: 700 }}
                  >
                    <SortHeader k="name" label={I18N.table.name[lang]} />
                  </TableCell>

                  <TableCell
                    sx={{ fontFamily: safeFont[lang], fontWeight: 700 }}
                  >
                    <SortHeader k="company" label={I18N.table.company[lang]} />
                  </TableCell>

                  <TableCell
                    sx={{ fontFamily: safeFont[lang], fontWeight: 700 }}
                  >
                    <SortHeader k="date" label={I18N.table.date[lang]} />
                  </TableCell>

                  <TableCell
                    sx={{ fontFamily: safeFont[lang], fontWeight: 700 }}
                  >
                    <SortHeader k="status" label={I18N.table.status[lang]} />
                  </TableCell>

                  <TableCell
                    align="center"
                    sx={{
                      fontFamily: safeFont[lang],
                      fontWeight: 700,
                      width: 72,
                    }}
                  >
                    {I18N.table.view[lang]}
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {filteredSorted.map((r) => {
                  const inf = r?.info || null;
                  const name = getInfoField(inf, "姓名") || "-";
                  const company = getInfoField(inf, "公司名稱") || "-";
                  const date = r?.inquiredDate || r?.finishedDate || "-";
                  const s = r?.status || "unknown";

                  return (
                    <TableRow
                      key={r.inquiryID}
                      hover
                      sx={{ "& td": { py: 1.25 } }}
                    >
                      <TableCell
                        sx={{ fontFamily: safeFont[lang], fontWeight: 700 }}
                      >
                        {safe(r.inquiryID)}
                      </TableCell>

                      <TableCell sx={{ fontFamily: safeFont[lang] }}>
                        {safe(name)}
                      </TableCell>

                      <TableCell sx={{ fontFamily: safeFont[lang] }}>
                        {safe(company)}
                      </TableCell>

                      <TableCell sx={{ fontFamily: safeFont[lang] }}>
                        {safe(date)}
                      </TableCell>

                      <TableCell sx={{ fontFamily: safeFont[lang] }}>
                        <Chip
                          size="small"
                          label={statusLabel(lang, s)}
                          color={statusChipColor(s)}
                          variant={s === "pending" ? "outlined" : "filled"}
                          sx={{ fontFamily: safeFont[lang], fontWeight: 700 }}
                        />
                      </TableCell>

                      <TableCell align="center">
                        <Stack
                          direction="row"
                          spacing={0.75}
                          justifyContent="center"
                          alignItems="center"
                        >
                          <Tooltip
                            title={I18N.seeMore[lang]}
                            arrow
                            enterDelay={200}
                          >
                            <IconButton
                              size="small"
                              onClick={() => handleView(r)}
                              aria-label="view"
                              sx={{
                                border: "1px solid rgba(0,0,0,0.12)",
                                borderRadius: 2,
                              }}
                            >
                              <VisibilityRoundedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>

                          {s === "checkedout" ? (
                            <Tooltip
                              title={I18N.cancel.button[lang]}
                              arrow
                              enterDelay={200}
                            >
                              <IconButton
                                size="small"
                                onClick={() => handleCancelOpen(r)}
                                aria-label="cancel inquiry"
                                sx={{
                                  border: "1px solid rgba(211,47,47,0.35)",
                                  borderRadius: 2,
                                  color: "error.main",
                                }}
                              >
                                <CancelRoundedIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          ) : null}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <Dialog
        open={Boolean(cancelTarget)}
        onClose={handleCancelClose}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle
          sx={{
            px: 2.5,
            py: 1.75,
            fontFamily: safeFont[lang],
            fontWeight: 700,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          {I18N.cancel.title[lang]}

          <IconButton
            size="small"
            onClick={handleCancelClose}
            disabled={cancelLoading}
            sx={{ border: "1px solid rgba(0,0,0,0.12)", borderRadius: 2 }}
          >
            <CloseRoundedIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <Divider />

        <DialogContent sx={{ p: 2.5 }}>
          <Typography
            sx={{
              fontFamily: safeFont[lang],
              fontWeight: 700,
              mb: 1.25,
              lineHeight: 1.7,
            }}
          >
            {I18N.cancel.message[lang]}
          </Typography>

          <Paper
            variant="outlined"
            sx={{
              p: 1.5,
              borderRadius: 2,
              bgcolor: "rgba(0,0,0,0.02)",
              mb: 2,
            }}
          >
            <Typography
              sx={{
                fontFamily: safeFont[lang],
                fontSize: 13,
                color: "text.secondary",
                fontWeight: 700,
                mb: 0.5,
              }}
            >
              {I18N.table.inquiryID[lang]}
            </Typography>
            <Typography
              sx={{
                fontFamily: safeFont[lang],
                fontWeight: 700,
                wordBreak: "break-word",
              }}
            >
              {safe(cancelTarget?.inquiryID)}
            </Typography>
          </Paper>

          {cancelError ? (
            <Typography
              sx={{
                fontFamily: safeFont[lang],
                color: "error.main",
                fontWeight: 700,
                mb: 2,
              }}
            >
              {cancelError}
            </Typography>
          ) : null}

          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button
              variant="outlined"
              onClick={handleCancelClose}
              disabled={cancelLoading}
              sx={{ fontFamily: safeFont[lang], borderRadius: 2 }}
            >
              {I18N.dialog.close[lang]}
            </Button>

            <Button
              variant="contained"
              color="error"
              onClick={handleCancelConfirm}
              disabled={cancelLoading}
              sx={{ fontFamily: safeFont[lang], borderRadius: 2 }}
            >
              {I18N.cancel.confirm[lang]}
            </Button>
          </Stack>
        </DialogContent>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={open} onClose={closeDialog} fullWidth maxWidth="md">
        <DialogTitle
          sx={{
            px: { xs: 2, sm: 2.5 },
            py: 1.75,
            fontFamily: safeFont[lang],
            fontWeight: 700,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                fontFamily: safeFont[lang],
                fontWeight: 700,
                fontSize: { xs: 18, sm: 20 },
                lineHeight: 1.2,
                wordBreak: "break-word",
              }}
            >
              {I18N.dialog.title[lang]}
              {selected?.inquiryID ? ` (${selected.inquiryID})` : ""}
            </Typography>
          </Box>

          <IconButton
            size="small"
            onClick={closeDialog}
            sx={{ border: "1px solid rgba(0,0,0,0.12)", borderRadius: 2 }}
          >
            <CloseRoundedIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <Divider />

        <DialogContent sx={{ p: { xs: 2, sm: 2.5 } }}>
          <Typography
            sx={{ fontFamily: safeFont[lang], fontWeight: 700, mb: 1 }}
          >
            {I18N.dialog.sectionInfo[lang]}
          </Typography>

          <Paper
            variant="outlined"
            sx={{
              p: { xs: 1.75, sm: 2 },
              borderRadius: 2,
              bgcolor: "rgba(0,0,0,0.015)",
              mb: 2,
            }}
          >
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: isMdUp
                  ? "repeat(3, minmax(0, 1fr))"
                  : "1fr",
                gap: 2,
              }}
            >
              <KV
                label={I18N.dialog.fields.name[lang]}
                value={getInfoField(info, "姓名")}
                fontFamily={safeFont[lang]}
              />
              <KV
                label={I18N.dialog.fields.company[lang]}
                value={getInfoField(info, "公司名稱")}
                fontFamily={safeFont[lang]}
              />
              <KV
                label={I18N.dialog.fields.phone[lang]}
                value={getInfoField(info, "聯絡電話")}
                fontFamily={safeFont[lang]}
              />

              <KV
                label={I18N.dialog.fields.email[lang]}
                value={getInfoField(info, "電子郵件")}
                fontFamily={safeFont[lang]}
              />
              <KV
                label={I18N.dialog.fields.county[lang]}
                value={getInfoField(info, "縣市")}
                fontFamily={safeFont[lang]}
              />
              <KV
                label={I18N.dialog.fields.address[lang]}
                value={getInfoField(info, "地址")}
                fontFamily={safeFont[lang]}
              />

              <KV
                label={I18N.dialog.fields.industry[lang]}
                value={getInfoField(info, "產業別")}
                fontFamily={safeFont[lang]}
              />
            </Box>

            {String(getInfoField(info, "訊息") || "").trim() ? (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography
                  sx={{
                    fontFamily: safeFont[lang],
                    fontWeight: 700,
                    mb: 0.5,
                    color: "text.secondary",
                    fontSize: 13,
                  }}
                >
                  {I18N.dialog.fields.message[lang]}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: safeFont[lang],
                    whiteSpace: "pre-wrap",
                    fontWeight: 600,
                  }}
                >
                  {safe(getInfoField(info, "訊息"))}
                </Typography>
              </>
            ) : null}
          </Paper>

          <Typography
            sx={{ fontFamily: safeFont[lang], fontWeight: 700, mb: 1 }}
          >
            {I18N.dialog.sectionItems[lang]}
          </Typography>

          {isMobile ? (
            <Stack spacing={1.25}>
              {(items || []).map((it, idx) => (
                <DialogItemCard
                  key={`${it.productID}_${idx}`}
                  it={it}
                  lang={lang}
                  safeFont={safeFont}
                  onGoProduct={() => goToProduct(it)}
                />
              ))}
            </Stack>
          ) : (
            <Paper
              variant="outlined"
              sx={{ overflow: "hidden", borderRadius: 2 }}
            >
              <TableContainer sx={{ maxHeight: 420 }}>
                <Table size="small" stickyHeader sx={{ minWidth: 760 }}>
                  <TableHead>
                    <TableRow sx={{ "& th": { bgcolor: "rgba(0,0,0,0.03)" } }}>
                      <TableCell
                        sx={{
                          fontFamily: safeFont[lang],
                          fontWeight: 700,
                          width: 110,
                        }}
                      >
                        {I18N.dialog.itemsTable.image[lang]}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontFamily: safeFont[lang],
                          fontWeight: 700,
                          width: 170,
                        }}
                      >
                        {I18N.dialog.itemsTable.productID[lang]}
                      </TableCell>
                      <TableCell
                        sx={{ fontFamily: safeFont[lang], fontWeight: 700 }}
                      >
                        {I18N.dialog.itemsTable.productName[lang]}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontFamily: safeFont[lang],
                          fontWeight: 700,
                          width: 90,
                        }}
                        align="center"
                      >
                        {I18N.dialog.itemsTable.qty[lang]}
                      </TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {(items || []).map((it, idx) => {
                      const thumb = resolveImgUrl(
                        it?.image || it?.images?.[0] || "",
                      );
                      return (
                        <TableRow key={`${it.productID}_${idx}`} hover>
                          <TableCell>
                            <Box
                              component="img"
                              src={thumb}
                              alt={safe(it?.productName)}
                              onError={(e) =>
                                (e.currentTarget.src = PLACEHOLDER_IMG)
                              }
                              sx={{
                                width: 78,
                                height: 58,
                                objectFit: "contain",
                                display: "block",
                                borderRadius: 1,
                                border: "1px solid",
                                borderColor: "divider",
                                bgcolor: "#fff",
                                p: 0.5,
                              }}
                            />
                          </TableCell>

                          <TableCell
                            sx={{ fontFamily: safeFont[lang], fontWeight: 400 }}
                          >
                            {safe(it.productID)}
                          </TableCell>

                          <TableCell sx={{ fontFamily: safeFont[lang] }}>
                            <MLink
                              component="button"
                              underline="hover"
                              onClick={() => goToProduct(it)}
                              sx={{
                                fontFamily: safeFont[lang],
                                fontWeight: 400,
                                textAlign: "left",
                                cursor: "pointer",
                                color: "primary.main",
                                overflowWrap: "anywhere",
                              }}
                            >
                              {safe(it.productName)}
                            </MLink>
                          </TableCell>

                          <TableCell
                            sx={{ fontFamily: safeFont[lang], fontWeight: 400 }}
                            align="center"
                          >
                            {safe(it.qty)}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}

          <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2 }}>
            <Button
              variant="outlined"
              onClick={closeDialog}
              sx={{ fontFamily: safeFont[lang], borderRadius: 2, px: 2.5 }}
            >
              {I18N.dialog.close[lang]}
            </Button>
          </Stack>
        </DialogContent>
      </Dialog>
    </Box>
  );
}
